import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Heart, Package, Shield, Clock, Users, Home } from 'lucide-react';
import { mockData } from '../mock';

const Services = () => {
  const { services } = mockData;

  const detailedServices = [
    {
      icon: Heart,
      title: "Pet Food Pantry",
      description: "Our food pantry provides essential nutrition for pets in need. We stock a variety of food for dogs, cats, and other household pets.",
      color: { bg: 'bg-red-100', text: 'text-red-600' },
      details: [
        "Dog food (all sizes and life stages)",
        "Cat food (dry and wet)",
        "Special dietary options",
        "Treats and supplements"
      ]
    },
    {
      icon: Package,
      title: "Donation Collection",
      description: "We accept a wide range of pet supplies to help families care for their four-legged companions.",
      color: { bg: 'bg-olive-100', text: 'text-olive-600' },
      details: [
        "Food and supplies",
        "Toys and enrichment items",
        "Bedding and comfort items",
        "Medical and hygiene supplies"
      ]
    },
    {
      icon: Shield,
      title: "Emergency Pet Support",
      description: "Quick assistance during unexpected circumstances to prevent pets from entering shelters.",
      color: { bg: 'bg-amber-100', text: 'text-amber-600' },
      details: [
        "Emergency food supplies",
        "Temporary support items",
        "Connection to resources",
        "Compassionate assistance"
      ]
    },
    {
      icon: Home,
      title: "Keeping Pets Home",
      description: "Our primary goal is ensuring pets stay with their loving families, no matter the challenges.",
      color: { bg: 'bg-mauve-200', text: 'text-mauve-700' },
      details: [
        "Preventing shelter surrender",
        "Preventing homelessness",
        "Supporting pet owners",
        "Building stronger bonds"
      ]
    },
    {
      icon: Users,
      title: "Community Support",
      description: "We work with local families to provide ongoing support and resources.",
      color: { bg: 'bg-olive-100', text: 'text-olive-600' },
      details: [
        "Airdrie area priority",
        "Surrounding area assistance",
        "No-judgment help",
        "Confidential services"
      ]
    },
    {
      icon: Clock,
      title: "Flexible Assistance",
      description: "We understand every situation is unique and offer flexible support options.",
      color: { bg: 'bg-amber-100', text: 'text-amber-600' },
      details: [
        "One-time help",
        "Ongoing support",
        "Customized assistance",
        "Responsive service"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-gradient-to-b from-mauve-100 to-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-darkslate-700 mb-4">
            Our Services
          </h1>
          <p className="text-lg text-darkslate-500">
            Comprehensive support to keep pets with their loving families.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {detailedServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-2 border-mauve-200 hover:border-olive-400 transition-all duration-300 hover:shadow-lg">
                  <CardHeader>
                    <div className={`w-12 h-12 ${service.color.bg} rounded-lg flex items-center justify-center mb-3`}>
                      <IconComponent className={`h-6 w-6 ${service.color.text}`} />
                    </div>
                    <CardTitle className="text-xl text-darkslate-700">{service.title}</CardTitle>
                    <CardDescription className="text-darkslate-500">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start text-sm text-darkslate-600">
                          <span className="text-olive-500 mr-2">•</span>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-mauve-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-darkslate-700 mb-4">
            Need Help or Want to Contribute?
          </h2>
          <p className="text-lg text-darkslate-500 mb-6">
            Whether you need assistance for your pet or want to donate supplies, we're here to help.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Services;
